package com.cs.ejobin.ui.screens.extra

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.cs.ejobin.i18n.Keys
import com.cs.ejobin.i18n.t
import com.cs.ejobin.model.Extra
import com.cs.ejobin.model.SkillCatalog
import com.cs.ejobin.platform.openExternalUrl
import com.cs.ejobin.ui.components.AvatarImage
import com.cs.ejobin.ui.components.ReadOnlySkillLabelChips
import com.cs.ejobin.ui.components.ScreenScaffold
import com.cs.ejobin.ui.theme.AppColors
import com.cs.ejobin.ui.theme.AppSurfaces
import com.cs.ejobin.vm.ExtraDetailViewModel
import ejobin.composeapp.generated.resources.Res
import ejobin.composeapp.generated.resources.whatsapp_logo
import org.jetbrains.compose.resources.painterResource

@Composable
fun ExtraDetailScreen(
    vm: ExtraDetailViewModel,
    extraId: String,
    onNeedPaywall: () -> Unit, // ✅ conservé pour ne rien casser (plus utilisé)
    onHome: () -> Unit,
    onOpenProfile: () -> Unit,
    onLogout: () -> Unit,
) {
    val state by vm.state.collectAsState()

    val primaryTextButtonColors = ButtonDefaults.textButtonColors(
        contentColor = AppColors.Primary
    )

    // ✅ Paiement désactivé : on charge toujours.
    LaunchedEffect(extraId) {
        vm.load(extraId)
    }

    ScreenScaffold(
        title = t(Keys.extra_title),
        topActions = {
            TextButton(onClick = onHome, colors = primaryTextButtonColors) {
                Text(t(Keys.home))
            }
            TextButton(onClick = onOpenProfile, colors = primaryTextButtonColors) {
                Text(t(Keys.profile_title))
            }
            TextButton(onClick = onLogout, colors = primaryTextButtonColors) {
                Text(t(Keys.logout))
            }
        },
        scrollable = true
    ) {
        when {
            state.isLoading -> LoadingBlock()
            state.error != null -> ErrorBlock(message = state.error ?: t(Keys.error_generic), onRetry = vm::retry)
            state.extra != null -> ExtraDetailContent(extra = state.extra!!)
            else -> EmptyBlock()
        }
    }
}

/*
@Composable
private fun LockedExtraDetail(onSubscribe: () -> Unit) { ... }
*/

@Composable
private fun ExtraDetailContent(extra: Extra) {
    val photoUrl = extra.photoUrl?.takeIf { it.isNotBlank() }
    val skillsLabels = extra.skills.map { s ->
        SkillCatalog.fromLabel(s.label)?.let { t(it.labelKey) } ?: s.label
    }

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                if (photoUrl != null) {
                    AvatarImage(
                        url = photoUrl,
                        size = 96.dp,
                        cornerRadius = 18.dp
                    )
                }

                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(
                        extra.name,
                        style = MaterialTheme.typography.titleLarge,
                        color = AppColors.TextPrimary
                    )

                    val availabilityText = if (extra.isAvailable) t(Keys.available) else t(Keys.unavailable)
                    Text(
                        availabilityText,
                        style = MaterialTheme.typography.bodyMedium,
                        color = if (extra.isAvailable) AppColors.Primary else AppColors.PrimaryLight
                    )
                }
            }

            HorizontalDivider()

            PhoneRowWithWhatsApp(
                label = t(Keys.phone_label),
                phone = extra.phone
            )

            // ✅ Description (lecture seule) - affichée uniquement si non vide
            val desc = extra.description?.trim().orEmpty()
            if (desc.isNotBlank()) {
                InfoRow(label = t(Keys.description), value = desc)
            }

            ReadOnlySkillLabelChips(
                title = t(Keys.skills_jobs),
                labels = skillsLabels,
                columns = 3,
                collapsedCount = 9
            )
        }
    }
}

@Composable
private fun PhoneRowWithWhatsApp(label: String, phone: String?) {
    val raw = phone?.trim().orEmpty()
    val waNumber = raw.toWhatsAppNumberOrNull()

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(label, style = MaterialTheme.typography.labelMedium, color = AppColors.TextPrimary)
            Text(raw.ifBlank { "—" }, style = MaterialTheme.typography.bodyLarge, color = AppColors.TextPrimary)
        }

        IconButton(
            onClick = { openExternalUrl("https://wa.me/$waNumber") },
            enabled = waNumber != null
        ) {
            Icon(
                painter = painterResource(Res.drawable.whatsapp_logo),
                contentDescription = "WhatsApp",
                tint = Color.Unspecified,
                modifier = Modifier.size(28.dp)
            )
        }
    }
}

fun String.toWhatsAppNumberOrNull(): String? {
    val s = this.trim()
    if (s.isBlank()) return null

    val cleaned = buildString {
        for (c in s) {
            if (c.isDigit() || c == '+') append(c)
        }
    }

    val normalized = when {
        cleaned.startsWith("+") -> cleaned.drop(1)
        cleaned.startsWith("00") -> cleaned.drop(2)
        else -> cleaned
    }

    val digits = normalized.filter { it.isDigit() }
    return digits.takeIf { it.isNotBlank() }
}

@Composable
private fun InfoRow(label: String, value: String) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(label, style = MaterialTheme.typography.labelMedium, color = AppColors.TextPrimary)
        Text(value, style = MaterialTheme.typography.bodyLarge, color = AppColors.TextPrimary)
    }
}

@Composable
private fun LoadingBlock() {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
        Spacer(Modifier.width(10.dp))
        Text(t(Keys.loading), color = AppColors.TextPrimary)
    }
}

@Composable
private fun ErrorBlock(message: String, onRetry: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(message, color = AppColors.TextPrimary)
            Button(onClick = onRetry, colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)) {
                Text(t(Keys.retry), color = AppColors.TextPrimary)
            }
        }
    }
}

@Composable
private fun EmptyBlock() {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                t(Keys.no_results_title),
                style = MaterialTheme.typography.titleMedium,
                color = AppColors.TextPrimary
            )
            Text(
                t(Keys.no_results_body),
                style = MaterialTheme.typography.bodyMedium,
                color = AppColors.TextPrimary
            )
        }
    }
}